import { IApprovalLevelConfig } from '../../features/oms/models/approval-workflow.model'
import { IInstallmentOption } from '../../features/oms/models/installment-details.model'
import { API_ERRORS } from './api-errors.constants'
import { IContractTermOption } from '../../features/oms/models/program-timeline.model'

export const ADD_ONS_CONSTANTS = {
    DEFAULT_MAX_SELECTION: 3,
} as const

export const APPROVAL_WORKFLOW_CONSTANTS = {
    DISCOUNT_THRESHOLDS: {
        AUTO_APPROVE: 10,
        SZM: 20,
        NSH: 40,
        NSH_CFO: 100,
    },
    APPROVAL_LEVEL_VALUES: {
        AUTO_APPROVE: 'auto-approve',
        SZM: 'szm',
        HAC: 'hac',
        NSH: 'nsh',
        NSH_CFO: 'nsh-cfo',
    },
    APPROVAL_LEVELS: [
        {
            value: 'auto-approve',
            label: 'Auto-approve (0-10% discount)',
            badgeClass: 'badge--success',
            badgeText: 'Auto-approved',
            discountRange: { min: 0, max: 10 },
        },
        {
            value: 'szm',
            label: 'SZM Approval (11-20% discount)',
            badgeClass: 'badge--warning',
            badgeText: 'SZM Approval',
            discountRange: { min: 11, max: 20 },
        },
        {
            value: 'nsh',
            label: 'NSH Approval (21-40% discount)',
            badgeClass: 'badge--danger',
            badgeText: 'NSH Approval',
            discountRange: { min: 21, max: 40 },
        },
        {
            value: 'nsh-cfo',
            label: 'NSH + CFO Approval (>40% discount)',
            badgeClass: 'badge--danger-dark',
            badgeText: 'NSH + CFO Approval',
            discountRange: { min: 41, max: 100 },
        },
    ] as IApprovalLevelConfig[],
} as const

export const CONTACT_BOOK_CONSTANTS = {
    MIN_REQUIRED_CONTACTS: 1,
    RECOMMENDED_CONTACTS: 2,
    PHONE_PATTERN: '^[0-9]{10}$',
    /** Subscriber digit count implied by {@link CONTACT_BOOK_CONSTANTS.PHONE_PATTERN}. */
    PHONE_MAX_DIGITS: 10,
    CONTACT_ROLES: [
        'Principal',
        'Vice Principal',
        'Academic Coordinator',
        'IT Head',
        'Administrator',
        'Teacher',
        'Other',
    ],
    FORM_CONTROLS: {
        FIRST_NAME: 'firstName',
        LAST_NAME: 'lastName',
        EMAIL: 'email',
        PHONE: 'phone',
        ROLE: 'role',
        CUSTOM_ROLE: 'customRole',
    },
    /** Dropdown value that triggers the free-text designation field */
    CONTACT_ROLE_OTHER: 'Other',
    DEFAULT_VALUES: {
        COUNTRY_CODE: 91,
        EMAIL_CONSENT: true,
        MOBILE_CONSENT: true,
        IS_SELECTED: false,
        IS_KDM: false,
        ROLE: '',
        PHONE: '',
        EMAIL: '',
    },
    ICON_TEXT: {
        CHECK_CIRCLE: 'check_circle',
        WARNING: 'warning',
    },
    KDM_CHECKBOX: {
        ARIA_LABEL: 'Mark as KDM',
    },
    UI_TEXT: {
        KDM_WARNING: 'Please select one user as the Key Decision Maker(KDM)',
    },
} as const

export const NOTES_TO_MANAGER_CONSTANTS = {
    MIN_LENGTH: 60,
} as const

export const DIALOG_MESSAGES = {
    DRAFT_SAVED: {
        title: 'Draft Saved',
        message: 'Your proposal draft has been saved successfully.',
        confirmText: 'OK',
    },
    SUBMIT_FOR_APPROVAL: {
        title: 'Submit for Approval',
        message: 'Are you sure you want to submit this proposal for approval?',
        confirmText: 'Submit',
        cancelText: 'Cancel',
    },
    AUTO_APPROVAL_SEND_TO_SCHOOL: {
        title: 'Send Proposal to School',
        message:
            'This proposal is auto-approved and will be sent to the school for review. Do you want to continue?',
        confirmText: 'Send',
    },
    PROPOSAL_SUBMITTED: {
        title: 'Proposal Submitted',
        message: 'Your proposal has been submitted for approval successfully.',
        confirmText: 'OK',
    },
} as const

export const INSTALLMENT_DETAILS_CONSTANTS = {
    MIN_FIRST_INSTALLMENT_PERCENTAGE: 20,
    DEFAULT_INSTALLMENTS: 2,
    AMOUNT_TOLERANCE: 100,
    UPDATE_DEBOUNCE_MS: 500,
    PERCENTAGE: 'percentage',
    DUE_DATE: 'dueDate',
    SECTION_TITLE_INSTALLMENTS: 'Installment Details',
    SECTION_TITLE_FULL_UPFRONT: 'Final Amount Payable',
    SCHEDULE_LABEL_FULL_UPFRONT: 'Payment due date',

    VALIDATION_MESSAGES: {
        FIRST_INSTALLMENT_MIN: 'First installment must be at least 20% of the total amount',
        INSTALLMENT_PERCENTAGE_ZERO: 'Installment percentage cannot be 0%',
        TOTAL_PERCENTAGE_MAX: 'Total percentage cannot exceed 100%',
        TOTAL_PERCENTAGE_100: 'Total percentage must equal 100%',
        AMOUNT_MISMATCH: 'Total calculated amount does not match the expected total amount',
        DUE_DATES_REQUIRED: 'All due dates must be specified',
        DUE_DATES_SEQUENTIAL:
            'Due dates must be in sequential order (each installment date must be after the previous one)',
        YEAR_BEYOND_CURRENT: 'Installment year cannot go beyond the current year',
    },

    INSTALLMENT_OPTIONS: [
        { value: 2, label: '2 Installments', description: 'Split into 2 payments' },
        { value: 3, label: '3 Installments', description: 'Split into 3 payments' },
        { value: 4, label: '4 Installments', description: 'Split into 4 payments' },
    ] as IInstallmentOption[],

    DATE_CONSTRAINTS: {
        getCurrentYear: () => new Date().getFullYear(),
        getMaxDueDate: () => new Date(new Date().getFullYear(), 11, 31), // December 31st
        getMinDueDate: () => new Date(), // Today
        getDefaultDueDate: () => new Date(new Date().getFullYear(), 11, 31),
    },
} as const

export const PRICING_CONSTANTS = {
    GST_RATE: 0.18,
    ROUNDING_DECIMALS: 2,
    LARGE_ORDER_THRESHOLD: 500000,
    MULTI_YEAR_CONTRACT_YEARS: 3,
    PRODUCT_TYPES: {
        ASSET: 'ASSET',
        MINDSPARK: 'MINDSPARK',
        CARES: 'CARES',
    } as const,
    // ✅ DISABLED: All discount percentages set to 0 per user request
    PROMO_PERCENTAGES: {
        LARGE_ORDER: 0, // 0.1,
        ADDON: 0, // 0.05,
        RTE: 0, // 0.1,
        DEMO: 0, // 1.0,
        MULTI_YEAR: 0, // 0.05,
        GENERIC: 0, // 0.03,
    } as const,
    PRICING_ICONS: {
        TRENDING_UP: 'trending_up',
        TRENDING_DOWN: 'trending_down',
        TRENDING_FLAT: 'trending_flat',
    },
} as const

export type ProductTypeKey = keyof typeof PRICING_CONSTANTS.PRODUCT_TYPES

export const PRODUCT_CONFIGURATION_CONSTANTS = {
    VALIDATION: {
        RTE_MAX_PERCENTAGE: 0.25,
        MIN_STUDENTS: 0,
        MAX_DISCOUNT: 100,
        MIN_DISCOUNT: 0,
    },
    PRICING: {
        DEFAULT_RATE: 100,
        MIN_PRICE: 0,
    },
    FORM: {
        MODES: ['Online', 'Offline', 'OMR'],
        ROUNDS: ['Summer', 'Winter', 'Both'],
        TEST_PACKS: ['3-test pack', '4-test pack'],
        TEST_TYPES: ['MCQ', 'MCQ+Subjective'],
    },
    UI_TEXT: {
        BUNDLE_TOOLTIP: 'A bundle is a group of grades/subjects under one product.',
        UNIT_PRICE_LABEL: 'Unit Price - after discount (per student)',
        ADD_BUNDLE: 'Add Bundle',
        ADDING_BUNDLE: 'Adding...',
    },
} as const

export const PROGRAM_TIMELINE_CONSTANTS = {
    DEFAULT_CONTRACT_YEARS: 1,

    CONTRACT_TERM_OPTIONS: [
        { value: 1, label: 'One year', description: 'Standard 1 year contract' },
        { value: 2, label: '18 month', description: '1.5 year contract' },
        { value: 3, label: 'Multi year', description: '3 year extended contract' },
    ] as IContractTermOption[],

    CONTRACT_DURATION_MAPPING: {
        1: { years: 1, months: 0 }, // One year
        2: { years: 1, months: 6 }, // 18 months
        3: { years: 3, months: 0 }, // Multi-year (3 years)
    },

    ACADEMIC_YEAR_DURATION: { years: 1, months: 0 }, // Always 1 year for academic

    DATE_FORMAT: {
        DISPLAY: 'DD/MM/YYYY',
        ISO: 'YYYY-MM-DD',
    },

    ERROR_MESSAGES: {
        PROGRAM_BEFORE_ACADEMIC: 'Program cannot start before academic session.',
    },
} as const

export const PROPOSAL_CONSTANTS = {
    ALL_GRADES: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const,
    ICON_TEXT: { EXPAND_LESS: 'expand_less', EXPAND_MORE: 'expand_more' } as const,
} as const

/** RTE business rules for generate proposal (grade caps, eligibility). */
export const RTE_POLICY_CONSTANTS = {
    /** Highest grade (inclusive) that may carry RTE. Grades 9–10 and above are not eligible. */
    MAX_ELIGIBLE_GRADE: 8,
    RTE_MAX_FRACTION: 0.25,
} as const

export const TEST_PACK_CONFIG = {
    DEFAULT_TESTS_PER_PACK: 3,
    FOUR_TEST_PACK_TESTS: 4,
    THREE_TEST_PACK_TESTS: 3,
} as const

export const TEST_PACK_TYPES = {
    THREE_TEST: '3-test pack',
    FOUR_TEST: '4-test pack',
} as const

export const VALIDATION_CONSTANTS = {
    NAVIGATION_KEYS: [
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'Home',
        'End',
    ] as readonly string[],

    CONTROL_KEYS: ['a', 'c', 'v', 'x', 'z'] as readonly string[],
    DECIMAL_POINT: '.',
    DIGIT_PATTERN: /^\d$/,
    NON_DIGIT_PATTERN: /[^0-9]/g,
    INPUT_VALIDATION: {
        MIN_PERCENTAGE: 0,
        MAX_PERCENTAGE: 100,
        DECIMAL_PLACES: 2,
    },
}

export const VERTICAL_CONSTANTS = {
    DEFAULT_VERTICAL_ID: 1, // Hardcoded for now, will be changed once entire flow is completed
    DEFAULT_MEDIUM_ID: 1,
    DEFAULT_ORGANISATION_ID: 1752, // Default organisation ID for organisation details API
    VERTICAL_NAME: 'national',
    TYPE: 'SSF',
    SUPPORTED_VERTICALS: {
        NATIONAL: 1, // Only this vertical is supported in current implementation
    },

    LOADING_MESSAGES: {
        FETCHING_VERTICAL: 'Loading system configuration...',
    },

    ERROR_MESSAGES: {
        FETCH_FAILED: API_ERRORS.FETCH_FAILED,
        UNSUPPORTED_VERTICAL:
            'This system configuration is not supported in the current application.',
        NETWORK_ERROR: API_ERRORS.NETWORK_ERROR,
        INVALID_RESPONSE: API_ERRORS.INVALID_RESPONSE,
        SERVER_ERROR: API_ERRORS.SERVER_ERROR,
        UNAUTHORIZED: API_ERRORS.UNAUTHORIZED,
        ACCESS_DENIED: API_ERRORS.ACCESS_DENIED,
    },

    SUCCESS_MESSAGES: {
        VERTICAL_LOADED: 'System configuration loaded successfully',
    },
} as const

export const DEPT_CONSTANTS = {
    SALES: 1,
    ACAD: 2,
} as const

const SECOND_TIER_APPROVAL_BY_DEPT: Record<number, { value: string; roleLabel: string }> = {
    [DEPT_CONSTANTS.SALES]: {
        value: APPROVAL_WORKFLOW_CONSTANTS.APPROVAL_LEVEL_VALUES.SZM,
        roleLabel: 'SZM',
    },
    [DEPT_CONSTANTS.ACAD]: {
        value: APPROVAL_WORKFLOW_CONSTANTS.APPROVAL_LEVEL_VALUES.HAC,
        roleLabel: 'HAC',
    },
}

export function getSecondTierApprovalLevelValue(deptId: number): string {
    return (
        SECOND_TIER_APPROVAL_BY_DEPT[deptId]?.value ??
        APPROVAL_WORKFLOW_CONSTANTS.APPROVAL_LEVEL_VALUES.SZM
    )
}

export function getApprovalLevelsForDept(deptId: number): IApprovalLevelConfig[] {
    const secondTierConfig =
        SECOND_TIER_APPROVAL_BY_DEPT[deptId] ?? SECOND_TIER_APPROVAL_BY_DEPT[DEPT_CONSTANTS.SALES]

    return APPROVAL_WORKFLOW_CONSTANTS.APPROVAL_LEVELS.map((level) => {
        if (level.value !== APPROVAL_WORKFLOW_CONSTANTS.APPROVAL_LEVEL_VALUES.SZM) {
            return { ...level }
        }

        return {
            ...level,
            value: secondTierConfig.value,
            label: `${secondTierConfig.roleLabel} Approval (11-20% discount)`,
            badgeText: `${secondTierConfig.roleLabel} Approval`,
        }
    })
}

export const PRICE_UPLIFT_CONSTANTS = {
    TOLERANCE: 0.01,
} as const

export const UPLIFT_DIALOG_MESSAGES = {
    REMOVE_SAVED_UPLIFT: {
        title: 'Reset to catalog?',
        confirmText: 'Reset to catalog',
        cancelText: 'Keep uplift',
        messageTemplate: (catalogTotal: string, catalogUnitPrice: string) =>
            `Reset to catalog ${catalogTotal}` +
            (catalogUnitPrice ? ` (unit price ${catalogUnitPrice} per student)` : ''),
    },
    DISCARD_UNSAVED_UNIT_PRICE: {
        title: 'Discard unsaved unit price?',
        keepEditingText: 'Keep editing',
        revertSavedText: 'Revert to saved',
        removeText: 'Reset to catalog',
        messageTemplate: (unsavedUnitPrice: string, savedUnitPrice: string) =>
            `Unsaved: ${unsavedUnitPrice} per student\nLast saved: ${savedUnitPrice} per student`,
    },
} as const

export const PRODUCT_CONSTANTS = {
    /** Product IDs hidden from the proposal product selector (e.g. duplicate ASSET variants). */
    EXCLUDED_SELECTOR_PRODUCT_IDS: [6, 7] as readonly number[],

    LOADING_MESSAGES: {
        FETCHING_PRODUCTS: 'Loading available products...',
        REFRESHING_PRODUCTS: 'Refreshing product list...',
    },

    ERROR_MESSAGES: {
        FETCH_FAILED: 'Failed to load products. Please try again.',
        NETWORK_ERROR: 'Network error occurred while loading products.',
        INVALID_RESPONSE: 'Invalid response received from product service.',
        SERVER_ERROR: 'Server error occurred while loading products.',
        NO_PRODUCTS_FOUND: 'No products found for the selected vertical.',
    },

    SUCCESS_MESSAGES: {
        PRODUCTS_LOADED: 'Products loaded successfully',
        PRODUCTS_REFRESHED: 'Product list refreshed successfully',
    },
} as const

/**
 * Constants specific to the RTE Students section in Generate Proposal
 */
export const RTE_STUDENTS_CONSTANTS = {
    PROGRESS_BAR_COLORS: {
        PRIMARY: 'primary' as const,
        WARN: 'warn' as const,
    },
    ANIMATION_STATES: {
        EXPANDED: 'expanded' as const,
        COLLAPSED: 'collapsed' as const,
    },
} as const

export const PRICING_API_CONSTANTS = {
    DEFAULT_MEDIUM_ID: 1,
    PACK_VALUES: {
        MINDSPARK: 12,
        ASSET: {
            SUMMER_OR_WINTER: 1,
            BOTH: 2,
        },
        CARES: {
            TEST_PACKS: [2, 3, 4, 6, 8],
        },
    },
    PRINTING_OPTIONS: [
        { value: 'Printing by Ei', label: 'Printing by Ei', price: 150 },
        { value: 'Printing by School', label: 'Printing by School', price: 130 },
    ] as const,
    PRINTING: {
        UI: {
            BY_EI: 'Printing by Ei',
            BY_SCHOOL: 'Printing by School',
        },
        API: {
            EI: 'Ei',
            SCHOOL: 'School',
        },
    } as const,
    PRINTING_PRICES: {
        PRINTING_BY_EI: 150,
        PRINTING_BY_SCHOOL: 130,
    },
    LOADING_MESSAGES: {
        FETCHING_PRICING: 'Calculating pricing...',
        PROCESSING_REQUEST: 'Processing request...',
    },
    ERROR_MESSAGES: {
        NETWORK_ERROR: 'Network error occurred',
        INVALID_RESPONSE: 'Invalid response from server',
        CALCULATION_FAILED: 'Pricing calculation failed',
        INVALID_CONFIGURATION: 'Invalid configuration',
        SERVER_ERROR: 'Server error occurred',
    },
    SUCCESS_MESSAGES: {
        PRICING_CALCULATED: 'Pricing calculated successfully',
    },
} as const

// OMS routing segments used across modules (CRM and OMS)
export const OMS_ROUTES = {
    BASE: 'oms',
    GENERATE_PROPOSAL: 'generate-proposal',
    APPROVAL_PROPOSAL: 'approval-proposal',
    PROPOSAL_SUMMARY: 'proposal-summary',
} as const

export const PROPOSAL_ROUTE_STEPS = {
    CONFIG: 'config',
    REVIEW: 'review',
} as const

// Query param keys used within proposal generation flows
export const PROPOSAL_QUERY_PARAMS = {
    LEAD_ID: 'leadId',
    ORDER_IDS: 'order_ids',
    COPY_FROM_ORDER_ID: 'copyFromOrderId',
    CRITICAL_APPROVALS_EMPTY: 'critical_empty',
    ORDER_EDIT_STATUS: 'orderEditStatus',
    RETURN_TO: 'returnTo',
} as const

export const PROPOSAL_RETURN_TO = {
    LEAD_DASHBOARD: 'lead-dashboard',
    PROPOSAL_SUMMARY: 'proposal-summary',
} as const

export const PROPOSAL_CREATION_CONSTANTS = {
    CURRENCY: {
        DEFAULT: 'INR',
        CONVERSION_RATE: 1,
    },
    ORDER_STATUS: {
        PROPOSAL_CREATED: 'proposal_created',
        PROPOSAL_REVIEW_PENDING: 'proposal_review_pending',
        REJECTED: 'rejected',
    },
    DISCOUNT_TYPE: {
        PERCENTAGE: 'percentage',
        FIXED: 'fixed',
    },
    ACADEMIC_YEAR: {
        DEFAULT_ID: 1,
    },
    PROPOSAL_VALIDITY: {
        DAYS: 15, // Proposal valid for 15 days from creation
    },
    GST: {
        RATE: 18, // 18% GST
        APPLICABLE_PRODUCTS: ['MINDSPARK'], // Products that have GST
    },
    PAYMENT_MODE_MAPPING: {
        full_up_front: 'advance',
        installments: 'installments',
    },
    ERROR_MESSAGES: {
        PROPOSAL_CREATION_FAILED: 'Failed to create proposal. Please try again.',
        INVALID_DATA: 'Invalid data provided for proposal creation.',
        NETWORK_ERROR: 'Network error occurred while creating proposal.',
        PROPOSAL_NOTIFICATION_FAILED: 'Failed to send proposal to school. Please try again.',
    },
    SUCCESS_MESSAGES: {
        PROPOSAL_DRAFT_SAVED: 'Proposal saved as draft successfully!',
        PROPOSAL_CREATED: 'Proposal created successfully!',
        PROPOSAL_SUBMITTED: 'Proposal submitted for review successfully!',
        PROPOSAL_SENT_TO_SCHOOL: 'Proposal has been sent to the school successfully!',
    },
    LOADING_MESSAGES: {
        SAVING_DRAFT: 'Saving draft...',
        SUBMITTING_PROPOSAL: 'Submitting proposal for approval...',
        SENDING_PROPOSAL_TO_SCHOOL: 'Sending proposal to school...',
    },
    /** Substrings used to infer success from proposal-review notification API response message */
    NOTIFICATION_RESPONSE_MESSAGE_INDICATORS: {
        SUCCESS: 'success',
        SENT_SUCCESSFULLY: 'sent successfully',
    },
} as const

export const SIDEBAR_CONSTANTS = {
    WIDTH: {
        MIN: 300,
        MAX: 800,
        DEFAULT: 500,
    },
    ARIA_LABELS: {
        HIDE_SIDEBAR: 'Hide sidebar',
        SHOW_SIDEBAR: 'Show sidebar',
    },
    ICONS: {
        CHEVRON_RIGHT: 'chevron_right',
        CHEVRON_LEFT: 'chevron_left',
    },
} as const

export const MIN_TOTAL_ORDER_VALUE_EXCL_GST = 200000 as const
